import { Form, Formik } from "formik";
import React from "react";

import {
  currentinitialValues,
  validationSchemaNewRequest,
} from "@pages/User/NewRequest/resources/form/IForm";

import {
  ICustomerPayloadRequest,
  INewRequestPayloadRequest,
  IOrders,
  IProductPayloadRequest,
  IProductPayloadResponse,
} from "@pages/User/NewRequest/Model";
import { useForm } from "@pages/User/NewRequest/resources/context/FormContext";
import FormLayout from "@pages/User/NewRequest/resources/form/FormLayout";

import { ICreateOrderDataDTO } from "@modules/orders/dtos/ICreateOrderDataDTO";
import { ICreateOrderService } from "@modules/orders/models/ICreateOrderService";

import { useIoCContext } from "@context/IoCContext/IoCContext";

import useDialogAlert from "@hooks/useDialogAlert";

import { applyTaxAccordingFreight, formatTimezoneToDate } from "@utils/index";

import { Types } from "@ioc/types";
import { useFlags } from "flagsmith/react";
import { addMinutes, format } from "date-fns";
import { useRequestFormValidation } from "./FormValidateSubmit";
import { FreightType } from "@utils/interfaces";

interface RequestValidateProps {
  children?: React.ReactNode;
}

const RequestValidate: React.FC<RequestValidateProps> = () => {
  const flags = useFlags(["user_create_order_v2"]);

  const {
    productCart,
    selectedAddress,
    dataCustomer,
    selectedFilial,
    setLoadingSubmitButton,
    setNewOrders,
    setDeliveryDate,
  } = useForm();

  const { snackbar } = useDialogAlert();
  const iocContext = useIoCContext();

  const {
    isCartValid,
    isFormContextValid,
    isDataCustomerValid,
    isSelectedFilialValid,
    isSelectedAddressValid,
  } = useRequestFormValidation();

  return (
    <Formik
      key={"formik-orders"}
      initialValues={currentinitialValues}
      validationSchema={validationSchemaNewRequest}
      onSubmit={async (values) => {
        /*Logica de submissao do formulario */
        if (
          !isCartValid() ||
          !isFormContextValid() ||
          !isDataCustomerValid(dataCustomer) ||
          !isSelectedFilialValid(selectedFilial) ||
          !isSelectedAddressValid(selectedAddress)
        ) {
          return;
        }
        try {
          setLoadingSubmitButton(true);

          const customer: ICustomerPayloadRequest = {
            CNPJ: dataCustomer.CNPJ,
            distributionChannel: dataCustomer.DistributionChannel,
            division: dataCustomer.Division,
            id: dataCustomer.id,
          };

          const products: IProductPayloadRequest[] = productCart.map(
            (product) => {
              return {
                id: product.code!,
                description: product.name!,
                deadlinePayment: parseInt(product.paymentDaysSelected!),
                hashPrice: product.id!,
                payCond: parseInt(product.paymentDays!),
                price: applyTaxAccordingFreight(
                  values.freight,
                  product.value!,
                  product.priceFreight!
                ),
                quantity: parseFloat(product.amount!.replace(".", "")),
                payment: product.payment!,
              };
            }
          );

          const date = new Date(values.dateDelivery) || new Date();

          setDeliveryDate(date); // Data de entrega salva para ser usada como Query Params

          const deliveryDate = format(
            addMinutes(date, date.getTimezoneOffset()),
            "yyyy-MM-dd"
          );

          const newRequestpayload: INewRequestPayloadRequest = {
            address: { id: selectedAddress.key },
            comments: values.observation,
            includeCommentsInInvoice: values.commentsInInvoice,
            customer: customer,
            deliveryDate,
            filialID:
              dataCustomer.withdrawBasis[selectedFilial.filialIndex].filialID,
            freightType: values.freight as FreightType,
            products: products,
            withdrawBasisID:
              dataCustomer.withdrawBasis[selectedFilial.filialIndex]
                .shippingPoint,
          };

          const createOrderService = iocContext.serviceContainer.get<
            ICreateOrderService
          >(Types.Orders.ICreateOrderService);

          const response: ICreateOrderDataDTO = flags.user_create_order_v2
            .enabled
            ? await createOrderService.executeV2(newRequestpayload)
            : await createOrderService.execute(newRequestpayload);

          const newOrdersResponse: IOrders[] = response.response.map(
            (order, index) => {
              return {
                id: index.toString(),
                subsidiary:
                  dataCustomer.withdrawBasis[selectedFilial?.filialIndex]
                    .branchName,
                numberOrder: order.numberOrder ?? "Não gerada",
                product: order.product as IProductPayloadResponse,
                atemStatus: order.atemStatus ?? "error",
                deliveryDate: order.deliveryDate
                  ? formatTimezoneToDate(order.deliveryDate)
                  : "Não definido",
              };
            }
          );

          setNewOrders(newOrdersResponse);

          if (response.status === "error") {
            snackbar({
              message: "Alguns pedidos não foram efetuados",
              variant: "warning",
            });
          } else {
            snackbar({
              message: "Pedidos efetuados com sucesso!",
              variant: "success",
            });
          }
        } catch (error) {
          snackbar({
            message:
              "Ocorreu um erro ao realizar o pedido, tente novamente mais tarde",
            variant: "error",
          });
        } finally {
          setLoadingSubmitButton(false);
        }
      }}
    >
      {() => {
        return (
          <Form id={"formik-form"}>
            <FormLayout />
          </Form>
        );
      }}
    </Formik>
  );
};

export default RequestValidate;
